import React, {ReactNode, useMemo} from 'react';
import {Button, Tooltip, Space} from 'acud';
import {ButtonProps} from 'acud/lib/button';
import {TooltipProps} from 'acud/lib/tooltip';
import {OutlinedLeft} from 'acud-icon';
import * as tmp from 'react-router-dom';
import classnames from 'classnames';

import {ACUD_PRO_PREFIX} from '../../../_common/constants';
import {useLocale} from '../../../locale';

import './index.less';

// ignore waring `"export 'useNavigate' (imported as 'rc') was not found in 'react-router-dom'`
const rc = tmp as any;

const HEADER_PREFIX_CLS = `${ACUD_PRO_PREFIX}-page-layout__header`;

interface BackProps {
    text?: ReactNode;
    url?: string;
    goBack?: boolean;
    onClick?: () => void;
}

interface ActionsProps extends Omit<ButtonProps, 'onClick'> {
    key: string;
    text: React.ReactNode;
    tooltip?: TooltipProps;
    onClick?: (e: any, action: Omit<ActionsProps, 'onClick'>) => void;
}

export interface HeaderProps {
    back?: string | BackProps;
    title: React.ReactNode;
    desc?: React.ReactNode;
    actions?: ActionsProps[];
    custom?: {
        left?: React.ReactNode;
        right?: React.ReactNode;
    };
    size?: 'medium' | 'small';
    className?: string;
    style?: React.CSSProperties;
}

export interface HeaderInterface extends React.FC<HeaderProps> {}

const Header: HeaderInterface = props => {
    const [locale] = useLocale('PageLayout');

    const {back, title, desc, actions = [], custom, size, className, style} = props;

    const hooksHistory = rc.useHistory?.();
    const navigate = rc.useNavigate?.();
    const history = hooksHistory || navigate;

    const headerCls = classnames(
        HEADER_PREFIX_CLS,
        {[`${HEADER_PREFIX_CLS}__${size}`]: size},
        className
    );

    const backConfig: BackProps = useMemo(() => {
        if (!back) {
            return {};
        };
        const {text, url, goBack, onClick} = (typeof back === 'string' ? {url: back} : back) as BackProps;

        return {
            text: text ?? locale.back,
            url,
            goBack,
            onClick: onClick ?? (() => {
                const canGoBack = goBack && (() => {
                    const referrer = document?.referrer || '';
                    const {host, pathname} = (referrer ? new URL(referrer) : {}) as any;
                    return referrer && host === window.location.host && pathname !== '/login';
                })();
                if (canGoBack) {
                    history?.goBack ? history.goBack?.() : history?.(-1);
                    return;
                }
                url && (history?.push ? history.push(url) : history?.(url));
            })
        };
    }, [back, history, locale.back]);

    const handlAction = (e: any, action: ActionsProps) => {
        const {onClick, ...other} = action;
        onClick?.(e, other);
    };

    return (
        <div className={headerCls} style={style}>
            <div className={`${HEADER_PREFIX_CLS}-title-wrapper`}>
                <div className={`${HEADER_PREFIX_CLS}-left`}>
                    {backConfig.onClick && (
                        <div className={`${HEADER_PREFIX_CLS}-back`} onClick={backConfig.onClick}>
                            <span className={`${HEADER_PREFIX_CLS}-back-icon`}>
                                <OutlinedLeft width={16} />
                            </span>
                            <span className={`${HEADER_PREFIX_CLS}-back-text`}>
                                {backConfig.text}
                            </span>
                        </div>
                    )}
                    {title && (
                        <div className={`${HEADER_PREFIX_CLS}-title`}>
                            {title}
                        </div>
                    )}
                    {custom?.left && (
                        <div className={`${HEADER_PREFIX_CLS}-custom-left`}>
                            {custom?.left}
                        </div>
                    )}
                </div>
                <div className={`${HEADER_PREFIX_CLS}-right`}>
                    {actions.length > 0 && (
                        <Space size={12} className={`${HEADER_PREFIX_CLS}-actions`}>
                            {actions.map((action, index) => {
                                const {key = `k-${index}`, text, tooltip, ...otherProps} = action;
                                const btnComp = (
                                    <Button
                                        key={key}
                                        {...otherProps}
                                        onClick={e => handlAction(e, action)}
                                    >{text}
                                    </Button>
                                );
                                return tooltip ? (
                                    <Tooltip key={key} {...tooltip}>
                                        {btnComp}
                                    </Tooltip>
                                ) : btnComp;
                            })}
                        </Space>
                    )}
                    {custom?.right && (
                        <div className={`${HEADER_PREFIX_CLS}-custom-right`}>
                            {custom?.right}
                        </div>
                    )}
                </div>
            </div>
            {desc && (
                <div className={`${HEADER_PREFIX_CLS}-desc`}>
                    {desc}
                </div>
            )}
        </div>
    );
};

export default Header;